import { useState } from 'react';
import { ImageLightbox } from './ImageLightbox.tsx';
import { cdnSrcSet, cdnVariant } from '../lib/cdn.ts';
import type { PageImage } from '../lib/types.ts';

/**
 * How the image is framed in the page. Both keep the whole image visible (`object-contain`) —
 * these are maps and diagrams, where a crop loses content.
 *
 * - `card`: a fixed 3:2 card, so a two-column text/image grid keeps an even rhythm. Letterboxes
 *   images whose aspect differs.
 * - `natural`: the image keeps its uploaded aspect ratio and the frame shrink-wraps it, capped so
 *   it never dominates the page. Used where the image is the whole point.
 */
export type EnlargeableImageFrame = 'card' | 'natural';

export interface EnlargeableImageProps {
  readonly image: PageImage;
  readonly frame?: EnlargeableImageFrame;
  /** Tone behind the image — the section's `img` shade. Letterboxed images show it. */
  readonly bgClass?: string;
  /** Responsive `sizes` hint. Only used when the image has a rendition srcset. */
  readonly sizes?: string;
}

/**
 * An image the visitor can open full-screen, with a close icon in the top-right corner
 * (see {@link ImageLightbox}). Shows a small "Vis i fuld skærm" badge so the affordance is
 * visible rather than something you have to guess at.
 *
 * Prefers the uncropped original (`originalSrc`) in the page too, so what you see before clicking
 * matches what you get after — a rendition would be a focus-point crop.
 */
export function EnlargeableImage({ image, frame = 'card', sizes, bgClass = 'bg-mf-green/10' }: EnlargeableImageProps) {
  const [open, setOpen] = useState(false);

  const srcSet = image.originalSrc ? null : cdnSrcSet(image.src);
  const src = srcSet ? cdnVariant(image.src, 'tablet') : image.originalSrc ?? image.src;

  const frameClass = frame === 'natural'
    // Shrink-wraps the image: `w-fit` stops the button stretching across the section, and the
    // height cap keeps a tall map from pushing the rest of the page off screen.
    ? 'mx-auto block w-fit max-w-full'
    : 'block aspect-3/2 w-full';
  const imageClass = frame === 'natural'
    ? 'max-h-[70vh] w-auto max-w-full object-contain'
    : 'h-full w-full object-contain';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={image.alt ? `${image.alt} — vis i fuld skærm` : 'Vis billedet i fuld skærm'}
        className={`group relative overflow-hidden rounded-card ${bgClass} cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-mf-coral ${frameClass}`}
      >
        <img
          src={src}
          srcSet={srcSet ?? undefined}
          sizes={sizes}
          alt={image.alt}
          className={imageClass}
          loading="lazy"
        />
        <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-mf-black/70 px-3 py-1.5 text-xs font-medium text-white transition-colors group-hover:bg-mf-black/85">
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 3h6v6" />
            <path d="M9 21H3v-6" />
            <path d="M21 3l-7 7" />
            <path d="M3 21l7-7" />
          </svg>
          Vis i fuld skærm
        </span>
      </button>
      {open && <ImageLightbox image={image} onClose={() => setOpen(false)} />}
    </>
  );
}
